import { useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useGetAuthoringComponentQuery, type ManifestIntent, type Reference } from '../api/authoring'
import { fieldsSchema, offerLabel, typeOf, typeProse } from '../model/manifest'
import { dividingFields, recordTypeOf } from '../model/records'
import type { EditorProps } from './types'
import { shown } from '../api/useIntent'

interface Where {
  field: string
  equals: string | boolean
}

/**
 * Which of a list's items a block shows: all of them, or the ones whose
 * yes/no or choice field has one value.
 *
 * **It is its own control because a derived form cannot draw it.** The
 * filter's `equals` is untyped on the wire — a boolean against one field,
 * a string against another — so `IntentForm` has a box for `field` and
 * none for `equals`, and would send a filter missing its required half.
 * What `equals` may be is decided by the field, and the field is declared
 * by the *records*, which is a third document: the block refers to a
 * list, and the list holds a record type.
 *
 * **Nothing here names a type.** The list is the reference whose target
 * holds records, asked of the manifest, and the fields offered are the
 * booleans and enums of whatever that list holds.
 */
export function MembershipFilter({
  tenant,
  manifest,
  intent,
  member,
  fields,
  references,
  busy,
  send,
}: Pick<EditorProps, 'tenant' | 'manifest' | 'fields' | 'references' | 'busy' | 'send'> & {
  intent: ManifestIntent
  /** The payload member that carries the filter, and the field that stores it. */
  member: string
}) {
  const source = listOf(manifest, references)
  const collection = useGetAuthoringComponentQuery({ tenant, id: source?.id ?? '' }, { skip: !source })
  const recordType = recordTypeOf(manifest, source?.id ?? '', collection.data)
  const dividers = dividingFields(recordType ? fieldsSchema(manifest, recordType) : undefined)

  const stored = whereOf(fields[member])
  const storedKey = JSON.stringify(stored ?? null)
  const [draft, setDraft] = useState<Where | undefined>(stored)
  // Keyed by what is stored rather than by the object that holds it,
  // which is a new one on every render.
  useEffect(() => setDraft(JSON.parse(storedKey) ?? undefined), [storedKey])

  const field = dividers.find((entry) => entry.name === draft?.field)
  const complete = draft === undefined || (field !== undefined && field.values.some((value) => value === draft.equals))
  const dirty = JSON.stringify(draft ?? null) !== storedKey
  const items = recordType ? typeProse(manifest, recordType).plural.toLowerCase() : 'items'

  return (
    <Stack spacing={1.5}>
      <Typography variant="h3">{offerLabel(intent)}</Typography>
      {!source ? (
        <Typography variant="body2" color="text.secondary">
          Choose the list this block shows first. What it can be narrowed by depends on what the list holds.
        </Typography>
      ) : dividers.length === 0 && !stored ? (
        <Typography variant="body2" color="text.secondary">
          {collection.isLoading
            ? 'Loading…'
            : `This block shows all of the ${items}. They have no yes/no or choice field to narrow them by.`}
        </Typography>
      ) : (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <TextField
              select
              size="small"
              label="Show"
              value={draft ? draft.field : ''}
              disabled={busy}
              onChange={(event) =>
                setDraft(event.target.value === '' ? undefined : { field: event.target.value, equals: '' })
              }
              sx={{ minWidth: 220 }}
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            >
              <MenuItem value="">All of the {items}</MenuItem>
              {dividers.map((entry) => (
                <MenuItem key={entry.name} value={entry.name}>
                  Only where {entry.name}
                </MenuItem>
              ))}
              {/* A stored filter over a field the records no longer
                  declare is still what the block holds, so it is shown
                  as what it is rather than as "all of them". */}
              {draft && !field && (
                <MenuItem value={draft.field}>Only where {draft.field} (no longer a field)</MenuItem>
              )}
            </TextField>
            {draft && (
              <TextField
                select
                size="small"
                label="Is"
                value={field?.values.some((value) => value === draft.equals) ? String(draft.equals) : ''}
                disabled={busy || !field}
                onChange={(event) =>
                  setDraft({
                    field: draft.field,
                    equals: field?.values.find((value) => String(value) === event.target.value) ?? '',
                  })
                }
                sx={{ minWidth: 140 }}
              >
                {(field?.values ?? []).map((value) => (
                  <MenuItem key={String(value)} value={String(value)}>
                    {value === true ? 'yes' : value === false ? 'no' : value}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              disabled={busy || !dirty || !complete}
              // Absent rather than null to clear: the payload's member is
              // optional, and the fold reads its absence as no filter.
              onClick={() => send(intent.name, draft ? { [member]: draft } : {}).catch(shown)}
            >
              Save
            </Button>
            <Button disabled={busy || !dirty} onClick={() => setDraft(stored)}>
              Discard
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  )
}

/** The reference whose target holds records: the list this block shows. */
function listOf(manifest: EditorProps['manifest'], references: Reference[]): Reference | undefined {
  return references.find((reference) => typeOf(manifest, reference.type ?? '')?.containsRoles?.includes('record'))
}

function whereOf(stored: unknown): Where | undefined {
  if (!stored || typeof stored !== 'object') return undefined
  const { field, equals } = stored as Record<string, unknown>
  if (typeof field !== 'string') return undefined
  return { field, equals: typeof equals === 'boolean' ? equals : String(equals ?? '') }
}
